/**
 * MadSourcePill — single component, every MAD readout.
 *
 * Per DESIGN_PRINCIPLES §47 (single source of truth). Anywhere the app
 * surfaces a MAD depletion %, mount this pill next to the number so the
 * user knows whether the math is driven by live probes or modeled from
 * ET₀. When a new MAD source ships (e.g. "blended" once we weight both),
 * extend this one component and every screen updates at once.
 *
 * Props:
 *   mad — the full mad sub-object from /api/widget/snapshot (or any other
 *         endpoint that forwards `compute_mad_state` verbatim). Pass the
 *         WHOLE object — not destructured keys — so new fields propagate.
 *   size — 'sm' (default, dashboard tiles) | 'xs' (for tight rows)
 */
import React from 'react';

export const MadSourcePill = ({ mad, size = 'sm' }) => {
  if (!mad || !mad.source) return null;
  const isXs = size === 'xs';
  const pad = isXs ? 'px-1.5 py-px text-[9px]' : 'px-2 py-0.5 text-[10px]';

  if (mad.source === 'live_probe') {
    const count = mad.probe_count_in_ground ?? null; // optional — set by /api/widget/snapshot
    const vwc = mad.probe_vwc_pct;
    const label = count && count > 0
      ? `${count} live probe${count === 1 ? '' : 's'}`
      : 'live probe';
    return (
      <span
        data-testid="mad-source-pill"
        data-source="live_probe"
        title={vwc != null
          ? `Driven by live VWC reading ${Math.round(vwc)}% from your buried probe${count > 1 ? 's' : ''}`
          : 'Driven by your live soil probes'}
        className={`inline-flex items-center gap-1 rounded-full bg-emerald-100 dark:bg-emerald-900/60 text-emerald-700 dark:text-emerald-200 font-semibold ${pad}`}
      >
        <span className="w-1 h-1 rounded-full bg-emerald-500 animate-pulse" />
        {label}
        {vwc != null && !isXs && (
          <span className="ml-0.5 font-mono tabular-nums opacity-80">
            · {Math.round(vwc)}% VWC
          </span>
        )}
      </span>
    );
  }

  if (mad.source === 'et_bucket') {
    return (
      <span
        data-testid="mad-source-pill"
        data-source="et_bucket"
        title="No probes connected — depletion modeled from ET₀ minus rain"
        className={`inline-flex items-center rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 font-semibold ${pad}`}
      >
        modeled from ET₀
      </span>
    );
  }

  return null;
};

export default MadSourcePill;
